import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"

const normalizeText = (value, fallback = "(not set)") => {
  if (typeof value !== "string") return fallback
  const trimmed = value.trim()
  return trimmed ? trimmed : fallback
}

const isUnassignedChannelGroup = (value) => {
  if (typeof value !== "string") return false
  return value.trim().toLowerCase() === "unassigned"
}

const parseSessionCount = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value)
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.round(numeric) : 0
}

const buildUnassignedRows = (rows) => {
  const aggregate = new Map()
  for (const row of rows) {
    if (!isUnassignedChannelGroup(row?.channel_group)) continue
    const source = normalizeText(row?.source)
    const medium = normalizeText(row?.medium)
    const sessions = parseSessionCount(row?.session_count)
    if (sessions <= 0) continue
    const key = `${source}\u241f${medium}`
    const existing = aggregate.get(key)
    if (existing) {
      existing.session_count += sessions
    } else {
      aggregate.set(key, { source, medium, session_count: sessions })
    }
  }
  return Array.from(aggregate.values()).sort(
    (a, b) => b.session_count - a.session_count
  )
}

export default function GA4UnassignedChannelGroup() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [rows, setRows] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) {
      console.debug("[GA4][UnassignedChannelGroup] Waiting for property to load", {
        propertyId,
      })
      return
    }

    if (propertyError || !propInfo?.id) {
      console.error(
        "[GA4][UnassignedChannelGroup] Unable to load property context",
        {
          propertyId,
          propertyError,
          hasPropInfo: Boolean(propInfo),
        }
      )
      setRows([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        console.log("[GA4][UnassignedChannelGroup] Loading unassigned rows", {
          propertyId,
          ga4PropertyId: propInfo.id,
        })
        const query = supabase
          .from("ga4_channel_grouping_audit_results")
          .select("channel_group, source, medium, session_count")
          .eq("ga4_property_id", propInfo.id)
          .ilike("channel_group", "Unassigned")
          .order("session_count", { ascending: false })
        const { data: resultRows, error } = await query
        if (error) throw error
        console.log("[GA4][UnassignedChannelGroup] Raw rows loaded", {
          propertyId,
          ga4PropertyId: propInfo.id,
          rawRowCount: Array.isArray(resultRows) ? resultRows.length : 0,
        })
        if (cancelled) return
        const unassignedRows = buildUnassignedRows(resultRows || [])
        console.log("[GA4][UnassignedChannelGroup] Processed rows", {
          propertyId,
          ga4PropertyId: propInfo.id,
          unassignedRowCount: unassignedRows.length,
        })
        setRows(unassignedRows)
      } catch (err) {
        if (cancelled) return
        console.error(
          "Failed to load GA4 unassigned channel group sessions",
          {
            propertyId,
            ga4PropertyId: propInfo.id,
            error: err,
          }
        )
        setRows([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo, propInfo?.id, propertyId, propertyLoading, propertyError])

  const loading = propertyLoading || auditLoading

  return (
    <div className="page-container">
      <h2>Unassigned Channel Group</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load unassigned channel group data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
              <p style={{ marginTop: "0.5rem" }}>
                Sessions attributed to the GA4 primary channel group of{" "}
                <strong>Unassigned</strong>, grouped by session source and medium.
              </p>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          {rows.length ? (
            <table className="ui-table" style={{ width: "100%", marginTop: "1rem" }}>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Medium</th>
                  <th>Sessions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source}-${r.medium}-${i}`}>
                    <td>{r.source}</td>
                    <td>{r.medium}</td>
                    <td>{r.session_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ marginTop: "1rem" }}>
              No sessions were classified as Unassigned during the selected period.
            </p>
          )}
        </>
      )}
    </div>
  )
}
